import React, { CSSProperties, ReactNode, RefObject } from 'react';

export interface Geometry extends Coords {
  /** Top window location (generated via calcAnchor) */
  top: number;
  /** Bottom window location (generated via calcAnchor) */
  bottom: number;
  /** Left window location (generated via calcAnchor) */
  left: number;
  /** Right window location (generated via calcAnchor) */
  right: number;
}

export interface Coords {
  /** Horizontal quadrant location (generated via calcAnchor) */
  x?: 0 | 1 | 2;
  /** Vertical quadrant location (generated via calcAnchor) */
  y?: 0 | 1;
}

export interface ForcedAlignment {
  /** Override horizontal popout positioning */
  xAlign?: 'left' | 'right' | 'center';
  /** Override vertical popout positioning */
  yAlign?: 'top' | 'bottom';
}

export interface PopoutProps extends ForcedAlignment {
  /** Component which the popout will be attached to */
  trigger: ReactNode;
  /** Contents of the popout */
  children: ReactNode;
  /** Width of the popout */
  width?: number;
  /** Additional props to be applied to the popout container */
  containerProps?: any[];
  /** Display the popout with a white background */
  light?: boolean;
  /** Wrapper Classname */
  className?: string;
  /** Classname to apply when popout is visible */
  activeClass?: string;
  /** Disables popout from being opened */
  disabled?: boolean;
}

export interface PopoutContainerProps {
  /** This is a prop generated by the parent component */
  type: 'flyout' | 'tooltip';
  /** This is a prop generated by the parent component */
  geometry?: Geometry;
  /** How wide the container should be */
  children: ReactNode;
  /** Any additional css properties */
  style?: React.CSSProperties;
  /** This is passed back to the parent component used in calculating geometry */
  containerRef?: RefObject<HTMLDivElement>;
  /** The width of the popping out element  */
  containerWidth?: number;
  /** Display the popout with a while background */
  light?: boolean;
  /** Override horizontal popout positioning */
  xAlign?: 'left' | 'right' | 'center';
  /** Override vertical popout positioning */
  yAlign?: 'top' | 'bottom';
}

const getGridLocation = (location: number, scale: number): 0 | 1 =>
  Math.floor(location / (scale / 2)) as 0 | 1;

const markerWidth = 20;

const calcHorizontal = (x: number, triggerWidth: number, popupWidth: number) =>
  [
    { left: triggerWidth !== 0 ? triggerWidth / 2 - markerWidth : markerWidth },
    { left: triggerWidth !== 0 ? (popupWidth / 2) * -1 + triggerWidth / 2 : markerWidth },
    { right: triggerWidth !== 0 ? triggerWidth / 2 - markerWidth : markerWidth }
  ][x];

const calcVertical = (y: number, triggerHeight: number) =>
  y === 0 ? { top: triggerHeight } : { bottom: triggerHeight };

export const forceAlignment = ({ xAlign, yAlign }: ForcedAlignment) => {
  const alignmentChart = {
    bottom: { y: 0 },
    top: { y: 1 },

    right: { x: 0 },
    center: { x: 1 },
    left: { x: 2 }
  };

  return {
    ...(xAlign ? alignmentChart[xAlign] : {}),
    ...(yAlign ? alignmentChart[yAlign] : {})
  };
};

export const calcAnchor = (elm: Element): Geometry => {
  const { top, left, bottom, right, height, width } = elm.getBoundingClientRect();
  return {
    top,
    left,
    right,
    bottom,
    x: getGridLocation(left + width / 2, window.innerWidth),
    y: getGridLocation(top + height / 2, window.innerHeight)
  };
};

export const generateStyles = (
  { x = 0, y = 0, right, left, top, bottom }: Geometry,
  popupWidth: number
): React.CSSProperties => {
  const horzCalc = right - left;
  const vertCalc = bottom - top;

  const horzPos =
    x !== undefined && right !== undefined && left !== undefined
      ? calcHorizontal(x, horzCalc, popupWidth)
      : {};

  const vertPos =
    y !== undefined && bottom !== undefined && top !== undefined ? calcVertical(y, vertCalc) : {};

  return {
    ...horzPos,
    ...vertPos,
    position: 'absolute',
    zIndex: 1
  };
};

export interface GeometryCss {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

export const getGeometry = (
  current: Element | null,
  { xAlign, yAlign }: ForcedAlignment
): GeometryCss => ({
  ...{
    bottom: 0,
    top: 0,
    left: 0,
    right: 0
  },
  ...(current ? calcAnchor(current) : {}),
  ...forceAlignment({ xAlign, yAlign })
});

// TODO: Look into rm'ing explicit typing when updating React based deps. Currently build errors out due to inferred type annotation
export const containerStyles = (
  containerWidth?: number,
  geometry?: Geometry,
  styleOverrides?: CSSProperties
): CSSProperties => ({
  ...(containerWidth && geometry ? generateStyles(geometry, containerWidth) : {}),
  ...(containerWidth ? { width: `${containerWidth}px` } : {}),
  ...styleOverrides
});

export const baseStyles = (
  visibility: boolean | undefined,
  containerWidth: number,
  geometry: Geometry
) => ({
  display: visibility ? 'inherit' : 'none',
  pointerEvents: visibility ? ('all' as 'all') : ('none' as 'none'),
  transform: calcTranslate(geometry.y, visibility),
  width: containerWidth
});

export const calcTranslate = (y: 0 | 1 | undefined, visibility: boolean | undefined) =>
  visibility ? `translateY(0)` : `translateY(${y === 0 ? '-' : ''}10px)`;
